import React, { useEffect, useState } from "react";
import {
  Dialog,
  Input,
  TextArea,
  Form,
  Button,
  Flex,
  Checkbox,
  Segment,
  Dropdown,
  Loader,
  Text,
} from "@fluentui/react-northstar";
import axios from "axios";
import { NotificationAlert } from "../../../../components/Notification/Notification";
import Logo from "../../../../Assets/images/logo.png";
import LogoLight from "../../../../Assets/images/logo.png";
import { toast } from "react-toastify";

import "./School.css";
import { useTranslation } from "react-i18next";
import { Select } from "antd";
import BranchesManagement from "./BranchesManagement";
import { useTrustedNetwork } from "../../../../context/TrustedNetworkContext";

// Enums for dropdown options
export const REGION_ENUM = Object.freeze({
  NORTHERN_AFRICA: "Northern Africa",
  SUB_SAHARAN_AFRICA: "Sub-Saharan Africa",
  NORTHERN_AMERICA: "Northern America",
  LATIN_AMERICA_AND_CARIBBEAN: "Latin America and the Caribbean",
  CENTRAL_ASIA: "Central Asia",
  EASTERN_ASIA: "Eastern Asia",
  SOUTH_EASTERN_ASIA: "South-Eastern Asia",
  SOUTHERN_ASIA: "Southern Asia",
  WESTERN_ASIA: "Western Asia",
  EASTERN_EUROPE: "Eastern Europe",
  NORTHERN_EUROPE: "Northern Europe",
  SOUTHERN_EUROPE: "Southern Europe",
  WESTERN_EUROPE: "Western Europe",
  AUSTRALIA_AND_NEW_ZEALAND: "Australia and New Zealand",
  MELANESIA: "Melanesia",
  MICRONESIA: "Micronesia",
  POLYNESIA: "Polynesia",
  OTHER: "Other",
});

export const LEVEL_ENUM = Object.freeze({
  PRE_SCHOOL: "Pre-School",
  PRIMARY: "Primary",
  MIDDLE: "Middle",
  SECONDARY: "Secondary",
  HIGHER_SECONDARY: "Higher Secondary",
  K12: "K-12",
  UNIVERSITY: "University",
  OTHER: "Other",
});

export const CATEGORY_ENUM = Object.freeze({
  URBAN: "Urban",
  RURAL: "Rural",
  SEMI_URBAN: "Semi-Urban",
  ELITE: "Elite",
  NGO: "NGO",
  OTHER: "Other",
});

export const TYPE_ENUM = Object.freeze({
  PUBLIC: "Public",
  PRIVATE: "Private",
  INTERNATIONAL: "International",
});

const regionOptions = Object.values(REGION_ENUM);
const levelOptions = Object.values(LEVEL_ENUM);
const categoryOptions = Object.values(CATEGORY_ENUM);
const typeOptions = Object.values(TYPE_ENUM);

const School = (props) => {
  const url = process.env.REACT_APP_EP_URL;
  const {
    isTrusted,
    updateIsTrusted,
    isLoading,
    isInitialLoading,
    schoolData: contextSchoolData,
  } = useTrustedNetwork();
  const [state, setState] = React.useState({
    SchoolLogo: null,
    NewSchoolLogo: null,
    SchoolName: "",
    ContactEmail: "",
    SchoolAddress: "",
    SchoolPhone: "",
    SchoolRegion: "",
    SchoolLevel: "",
    SchoolCategory: "",
    SchoolType: "",
  });
  const [load, setLoad] = React.useState(true);
  const [schoolData, setSchoolData] = React.useState(null);
  const [accDlg, setAccDlg] = React.useState(false);
  const [dataSaved, setDataSaved] = React.useState(false);
  const [onUpload, setOnUpload] = React.useState(false);
  const [contentEditable, setContentEditable] = React.useState(false);
  const [disclaimerDialog, setDisclaimerDialog] = useState({
    open: false,
    type: null, // 'enable' or 'disable'
  });
  const [branchesManagementOpen, setBranchesManagementOpen] = useState(false);

  const handleChange = () => {
    setDisclaimerDialog({
      open: true,
      type: isTrusted ? "disable" : "enable",
    });
  };

  const handleDisclaimerConfirm = async () => {
    try {
      await updateIsTrusted();
      setDisclaimerDialog({ open: false, type: null });
    } catch (error) {
      // Error handling is already done in updateIsTrusted
      console.error("Failed to update trusted status", error);
    }
  };

  const handleDisclaimerCancel = () => {
    setDisclaimerDialog({ open: false, type: null });
  };

  const { t } = useTranslation();
  const translation = t("trustedNetwork").schools.schoolInfo;
  const disclaimerTranslation = translation.disclaimer;

  const getSchoolInfo = () => {
    axios
      .get(`${url}/api/school/get-school-info${props.user.slug}`, {
        headers: {
          Authorization: "Bearer " + props.user.accessToken,
        },
      })
      .then((res) => {
        setSchoolData(res.data?.result[0] || null);
        setLoad(false);
        const result = res.data?.result[0] || {};

        setState({
          SchoolLogo: result.SchoolLogo,
          NewSchoolLogo: null,
          SchoolName: result.School,
          ContactEmail: result.ContactEmail,
          SchoolAddress: result.SchoolAddress,
          SchoolPhone: result.ContactPhone,
          SchoolRegion: result.region || "",
          SchoolLevel: result.level || "",
          SchoolCategory: result.category || "",
          SchoolType: result.type || "",
        });
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
        console.log(err);
        if (err.response?.status === 401) {
          setAccDlg(true);
          setLoad(false);
        }
      });
  };

  // Use context school data when available
  useEffect(() => {
    if (contextSchoolData && !isInitialLoading) {
      // Use data from context
      setSchoolData(contextSchoolData);
      setLoad(false);
      setState({
        SchoolLogo: contextSchoolData.SchoolLogo,
        NewSchoolLogo: null,
        SchoolName: contextSchoolData.School,
        ContactEmail: contextSchoolData.ContactEmail,
        SchoolAddress: contextSchoolData.SchoolAddress,
        SchoolPhone: contextSchoolData.ContactPhone,
        SchoolRegion: contextSchoolData.region || "",
        SchoolLevel: contextSchoolData.level || "",
        SchoolCategory: contextSchoolData.category || "",
        SchoolType: contextSchoolData.type || "",
      });
    }
  }, [contextSchoolData, isInitialLoading]);

  return (
    <div
      className="School container"
      style={{
        position: "relative",
        overflow: "visible", // changed from auto to visible
        height: props.isTabletOrMobileView
          ? "calc(100vh - 170px)"
          : "calc(100vh - 170px)",
        paddingTop: props.isTabletOrMobileView ? "0px" : "30px",
      }}
    >
      <Dialog
        open={accDlg}
        onConfirm={() => location.reload()}
        confirmButton="Refresh Now"
        content={
          <p className="refreshMsg">
            No big deal — but we need to refresh to show your content.
          </p>
        }
        header="Authorization expire"
      />

      <Dialog
        open={disclaimerDialog.open}
        onCancel={handleDisclaimerCancel}
        header={
          disclaimerDialog.type === "enable"
            ? translation.disclaimerHeaderEnable
            : translation.disclaimerHeaderDisable
        }
        content={
          disclaimerDialog.type === "enable" ? (
            <div style={{ padding: "10px 0" }}>
              <div style={{ marginBottom: "15px" }}>
                <h4 style={{ marginBottom: "8px", color: "#555" }}>
                  {disclaimerTranslation.ownershipHeader}
                </h4>
                <p
                  style={{
                    marginBottom: "10px",
                    lineHeight: "1.5",
                    fontSize: "14px",
                  }}
                >
                  {disclaimerTranslation.ownershipText}
                </p>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <h4 style={{ marginBottom: "8px", color: "#555" }}>
                  {disclaimerTranslation.guidelinesHeader}
                </h4>
                <p
                  style={{
                    marginBottom: "10px",
                    lineHeight: "1.5",
                    fontSize: "14px",
                  }}
                >
                  {disclaimerTranslation.guidelinesText}
                </p>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <h4 style={{ marginBottom: "8px", color: "#555" }}>
                  {disclaimerTranslation.accuracyHeader}
                </h4>
                <p
                  style={{
                    marginBottom: "10px",
                    lineHeight: "1.5",
                    fontSize: "14px",
                  }}
                >
                  {disclaimerTranslation.accuracyText}
                </p>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <h4 style={{ marginBottom: "8px", color: "#555" }}>
                  {disclaimerTranslation.userProtectionHeader}
                </h4>
                <p
                  style={{
                    marginBottom: "10px",
                    lineHeight: "1.5",
                    fontSize: "14px",
                  }}
                >
                  {disclaimerTranslation.userProtectionText}
                </p>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <h4 style={{ marginBottom: "8px", color: "#555" }}>
                  {disclaimerTranslation.thirdPartyHeader}
                </h4>
                <p
                  style={{
                    marginBottom: "10px",
                    lineHeight: "1.5",
                    fontSize: "14px",
                  }}
                >
                  {disclaimerTranslation.thirdPartyText}
                </p>
              </div>

              <p
                style={{
                  marginTop: "15px",
                  lineHeight: "1.5",
                  fontSize: "14px",
                }}
              >
                {disclaimerTranslation.finalText}
              </p>
            </div>
          ) : (
            <p style={{ lineHeight: "1.5" }}>
              {translation.disclaimerDisableContent}
            </p>
          )
        }
        confirmButton={{
          content: isLoading ? (
            <Flex gap="gap.small" vAlign="center">
              <Loader size="smallest" />
              <Text>
                {disclaimerDialog.type === "enable"
                  ? translation.disclaimerEnableButton
                  : translation.disclaimerDisableButton}
              </Text>
            </Flex>
          ) : disclaimerDialog.type === "enable" ? (
            translation.disclaimerEnableButton
          ) : (
            translation.disclaimerDisableButton
          ),
          primary: true,
          danger: disclaimerDialog.type === "disable",
          disabled: isLoading,
        }}
        cancelButton={{
          content: translation.cancel,
          disabled: isLoading,
        }}
        onConfirm={handleDisclaimerConfirm}
        style={{
          maxWidth: disclaimerDialog.type === "enable" ? "800px" : "500px",
          width: "100%",
        }}
      />

      <Form
        onSubmit={() => {
          setOnUpload(true);
          const data = new FormData();
          data.append(
            "SchoolLogo",
            state.NewSchoolLogo ? state.NewSchoolLogo : null
          );
          data.append("SchoolName", state.SchoolName);
          data.append("SchoolEmail", state.ContactEmail);
          data.append("SchoolAddress", state.SchoolAddress);
          data.append("SchoolPhone", state.SchoolPhone);
          data.append(
            "SchoolRegion",
            regionOptions.includes(state.SchoolRegion)
              ? state.SchoolRegion
              : null
          );
          data.append(
            "SchoolLevel",
            levelOptions.includes(state.SchoolLevel) ? state.SchoolLevel : null
          );
          data.append(
            "SchoolCategory",
            categoryOptions.includes(state.SchoolCategory)
              ? state.SchoolCategory
              : null
          );
          data.append(
            "SchoolType",
            typeOptions.includes(state.SchoolType) ? state.SchoolType : null
          );
          axios
            .put(
              `${url}/api/school/update-school-info${props.user.slug}`,
              data,
              {
                headers: {
                  Authorization: "Bearer " + props.user.accessToken,
                },
              }
            )
            .then((res) => {
              // console.log(res.data.dataw);
              setContentEditable(false);
              setSchoolData(res.data.data || null);
              props.getDb();
              setDataSaved(true);
              setOnUpload(false);
            })
            .catch((err) => {
              setContentEditable(false);
              toast.error(err?.response?.data?.message);
              NotificationAlert(err.response.data.message, "error");
              setOnUpload(false);
            });
        }}
        style={{ height: "auto" }}
      >
        <div className="school-header">
          <label htmlFor={"schoolLogo"} className="fw6 school-title">
            {t("setting").school.title[0]}
          </label>
          <div className="school-header-controls">
            <div className="trusted-toggle-container">
              <Checkbox
                toggle
                id="newCustomFieldChk"
                style={{ alignItems: "center" }}
                checked={isTrusted}
                onChange={handleChange}
                disabled={isInitialLoading}
              />
              <label
                className="form-check-label trusted-label"
                htmlFor="flexSwitchCheckChecked"
              >
                {translation.trustedEveryone}
              </label>
            </div>

            {/* School Branch Button - Moderate gap */}
            <Button
              content={translation.manageBranches}
              primary
              className="btn-blue mb-xl-4 manage-branches-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setBranchesManagementOpen(true);
              }}
            />
          </div>
        </div>
        <img
          htmlFor="schoolLogo"
          id="schoolLogo"
          src={
            state.SchoolLogo
              ? state.SchoolLogo + props.user.SASToken
              : schoolData?.SchoolLogo
              ? schoolData?.SchoolLogo + props.user.SASToken
              : props?.mode === "dark"
              ? LogoLight
              : Logo
          }
          className="schoolLogo"
          title={schoolData?.School || "School Logo"}
        />
        <Input
          type="file"
          id="schoolLogo"
          name="SchoolLogo"
          accept="image/png, image/svg, image/jpeg"
          onChange={async (event) => {
            if (contentEditable) {
              const selectedFile = event.target.files[0] || state.NewSchoolLogo;
              const reader = new FileReader();

              const imgtag = document.getElementById("schoolLogo");
              imgtag.title = selectedFile?.name || "School Logo";

              reader.onload = function (event) {
                imgtag.src = event.target.result;
              };

              reader.readAsDataURL(selectedFile);
              setState({
                ...state,
                NewSchoolLogo: await event.target.files[0],
              });
            }
          }}
          fluid
          disabled={!contentEditable}
        />
        <label htmlFor={"schoolName"} className="fw6">
          {translation.schoolName}
        </label>
        <Input
          type="text"
          id="schoolName"
          placeholder={translation.schoolNamePlaceholder}
          name="SchoolName"
          value={state.SchoolName}
          onChange={(event) => {
            contentEditable &&
              setState({ ...state, SchoolName: event.target.value });
          }}
          disabled={!contentEditable}
          fluid
        />
        <label htmlFor={"schoolEmail"} className="fw6">
          {translation.schoolEmail}
        </label>
        <Input
          type="text"
          id="schoolEmail"
          placeholder={translation.schoolEmailPlaceholder}
          name="SchoolEmail"
          value={state.ContactEmail}
          onChange={(event) => {
            contentEditable &&
              setState({ ...state, ContactEmail: event.target.value });
          }}
          fluid
          disabled={!contentEditable}
        />
        <label htmlFor={"schoolPhone"} className="fw6">
          {translation.schoolPhone}
        </label>
        <Input
          type="text"
          id="schoolPhone"
          placeholder={translation.schoolPhonePlaceholder}
          name="SchoolPhone"
          value={state.SchoolPhone}
          onChange={(event) => {
            contentEditable &&
              setState({ ...state, SchoolPhone: event.target.value });
          }}
          disabled={!contentEditable}
          fluid
        />
        <label htmlFor={"schoolAddress"} className="fw6">
          {translation.schoolAddress}
        </label>
        <TextArea
          type="text"
          id="schoolAddress"
          placeholder={translation.schoolAddressPlaceholder}
          name="SchoolAddress"
          value={state.SchoolAddress}
          onChange={(event) => {
            contentEditable &&
              setState({ ...state, SchoolAddress: event.target.value });
          }}
          disabled={!contentEditable}
          fluid
          // style={{ paddingBottom: "25px" }}
        />

        <label htmlFor={"region"} className="fw6">
          {translation.region}
        </label>
        <Select
          showSearch
          placeholder={translation.regionPlaceholder}
          optionFilterProp="children"
          onChange={(value) => {
            setState({ ...state, SchoolRegion: value });
          }}
          value={{ value: state.SchoolRegion, label: state.SchoolRegion }}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={regionOptions.map((r) => ({
            value: r,
            label: r,
          }))}
          disabled={!contentEditable}
        />

        <label htmlFor={"level"} className="fw6">
          {translation.level}
        </label>
        <Select
          showSearch
          placeholder={translation.levelPlaceholder}
          optionFilterProp="children"
          onChange={(value) => {
            setState({ ...state, SchoolLevel: value });
          }}
          value={{ value: state.SchoolLevel, label: state.SchoolLevel }}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={levelOptions.map((r) => ({
            value: r,
            label: r,
          }))}
          disabled={!contentEditable}
        />

        <label htmlFor={"category"} className="fw6">
          {translation.category}
        </label>
        <Select
          showSearch
          placeholder={translation.categoryPlaceholder}
          optionFilterProp="children"
          onChange={(value) => {
            setState({ ...state, SchoolCategory: value });
          }}
          value={{ value: state.SchoolCategory, label: state.SchoolCategory }}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={categoryOptions.map((r) => ({
            value: r,
            label: r,
          }))}
          disabled={!contentEditable}
        />

        <label htmlFor={"type"} className="fw6">
          {translation.type}
        </label>
        <Select
          showSearch
          placeholder={translation.typePlaceholder}
          optionFilterProp="children"
          onChange={(value) => {
            setState({ ...state, SchoolType: value });
          }}
          value={{ value: state.SchoolType, label: state.SchoolType }}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={typeOptions.map((r) => ({
            value: r,
            label: r,
          }))}
          disabled={!contentEditable}
        />

        <Flex gap="gap.smaller" className="school-footer-buttons">
          {contentEditable ? (
            <Button
              content={translation.cancelButton}
              secondary
              className="mb-xl-4 footer-btn cancel-btn"
              onClick={(e) => {
                e.preventDefault();
                setContentEditable(false);
                getSchoolInfo();
              }}
            />
          ) : (
            <Button
              content={translation.editButton}
              primary
              className="btn-blue mb-xl-4 footer-btn edit-btn"
              onClick={(e) => {
                e.preventDefault();
                setContentEditable(true);
              }}
            />
          )}
          {contentEditable && (
            <Button
              content={translation.saveButton}
              primary
              loading={onUpload}
              className="btn-blue mb-xl-4 footer-btn save-btn"
            />
          )}
        </Flex>
      </Form>

      {/* Branches Management Dialog */}
      <BranchesManagement
        open={branchesManagementOpen}
        onCancel={() => setBranchesManagementOpen(false)}
        user={props.user}
      />
    </div>
  );
};

export default School;
